import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import type { ListAuditLogResult, ListAuditLogParams, AuditLogEntry } from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, ClipboardList, AlertCircle } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { useGetMe } from "@workspace/api-client-react";
import { useDebounce } from "@/hooks/use-debounce";
import { Redirect } from "wouter";

const ACTION_LABELS: Record<string, string> = {
  "admin_settings.update": "Settings updated",
  "demo_data.reset": "Demo data reset",
  "email_provider.save": "Email provider saved",
  "email_provider.clear": "Email provider cleared",
  "mcp_server.create": "MCP server created",
  "mcp_server.update": "MCP server updated",
  "mcp_server.delete": "MCP server deleted",
  "mcp_token.create": "MCP token created",
  "mcp_token.revoke": "MCP token revoked",
  "salesforce_config.save": "Salesforce config saved",
  "salesforce_config.clear": "Salesforce config cleared",
  "salesforce.push": "Salesforce push queued",
};

const ACTION_VARIANTS: Record<string, "default" | "destructive" | "secondary" | "outline"> = {
  "demo_data.reset": "destructive",
  "email_provider.clear": "destructive",
  "mcp_server.delete": "destructive",
  "mcp_token.revoke": "destructive",
  "salesforce_config.clear": "destructive",
  "mcp_server.create": "default",
  "mcp_token.create": "default",
  "salesforce.push": "default",
  "mcp_server.update": "secondary",
  "admin_settings.update": "secondary",
  "email_provider.save": "secondary",
  "salesforce_config.save": "secondary",
};

const KNOWN_ACTIONS = Object.keys(ACTION_LABELS);

const PAGE_SIZE = 50;

async function fetchAuditLog(params: ListAuditLogParams): Promise<ListAuditLogResult> {
  const qs = new URLSearchParams();
  if (params.actorId) qs.set("actorId", params.actorId);
  if (params.actorEmail) qs.set("actorEmail", params.actorEmail);
  if (params.action) qs.set("action", params.action);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  qs.set("limit", String(params.limit ?? PAGE_SIZE));
  qs.set("offset", String(params.offset ?? 0));
  return customFetch<ListAuditLogResult>(`/api/admin/audit-log?${qs}`);
}

function getAuditLogQueryKey(params: ListAuditLogParams) {
  return ["/api/admin/audit-log", params] as const;
}

function ActionBadge({ action }: { action: string }) {
  const label = ACTION_LABELS[action] ?? action;
  const variant = ACTION_VARIANTS[action] ?? "outline";
  return <Badge variant={variant} className="text-xs whitespace-nowrap">{label}</Badge>;
}

function JsonSnippet({ data }: { data: Record<string, unknown> | null }) {
  if (!data) return null;
  return (
    <pre className="text-xs bg-muted rounded p-2 max-h-24 overflow-auto font-mono whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function AuditEntryRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = entry.before || entry.after;

  return (
    <div className="border rounded-lg p-3 space-y-1.5">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <ActionBadge action={entry.action} />
          <span className="text-sm text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
        </div>
        <span className="text-xs text-muted-foreground font-mono">{entry.actorEmail ?? "unknown"}</span>
      </div>
      <p className="text-sm">{entry.summary}</p>
      {hasDetails && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-primary underline underline-offset-2 hover:no-underline"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      )}
      {expanded && (
        <div className="space-y-2 mt-1">
          {entry.before && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Before</p>
              <JsonSnippet data={entry.before} />
            </div>
          )}
          {entry.after && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">After</p>
              <JsonSnippet data={entry.after} />
            </div>
          )}
          {entry.ip && (
            <p className="text-xs text-muted-foreground">IP: {entry.ip}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function AuditLogPage() {
  const { data: me, isLoading: isLoadingMe } = useGetMe();

  const [actorEmail, setActorEmail] = useState("");
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);

  const debouncedActorEmail = useDebounce(actorEmail, 300);
  const debouncedActorId = useDebounce(actorId, 300);

  useEffect(() => {
    setOffset(0);
  }, [debouncedActorEmail, debouncedActorId, action, from, to]);

  const params: ListAuditLogParams = {
    actorId: debouncedActorId.trim() || undefined,
    actorEmail: debouncedActorEmail.trim() || undefined,
    action: action !== "all" ? action : undefined,
    from: from || undefined,
    to: to || undefined,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: getAuditLogQueryKey(params),
    queryFn: () => fetchAuditLog(params),
    enabled: !!me?.isAdmin,
  });

  if (isLoadingMe) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!me?.isAdmin) {
    return <Redirect to="/dashboard" />;
  }

  const entries = data?.entries ?? [];
  const hasMore = entries.length === PAGE_SIZE;

  function clearFilters() {
    setActorEmail("");
    setActorId("");
    setAction("all");
    setFrom("");
    setTo("");
    setOffset(0);
  }

  const hasFilters =
    actorEmail !== "" ||
    actorId !== "" ||
    action !== "all" ||
    from !== "" ||
    to !== "";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList className="h-6 w-6" />
          Audit Log
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          A record of sensitive admin actions. Historical actions before this feature was enabled are not present.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Actor email</Label>
              <Input
                type="text"
                className="h-8 text-sm"
                placeholder="Search by email…"
                value={actorEmail}
                onChange={(e) => setActorEmail(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Actor ID</Label>
              <Input
                type="text"
                className="h-8 text-sm"
                placeholder="Exact actor ID…"
                value={actorId}
                onChange={(e) => setActorId(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Action</Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {KNOWN_ACTIONS.map((a) => (
                    <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">From date</Label>
                <Input
                  type="date"
                  className="h-8 text-sm"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To date</Label>
                <Input
                  type="date"
                  className="h-8 text-sm"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={clearFilters}
                className="h-8"
                disabled={!hasFilters}
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {isLoading && (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 text-destructive p-4 border border-destructive/30 rounded-lg bg-destructive/5">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <p className="text-sm">Failed to load audit log entries.</p>
          </div>
        )}

        {!isLoading && !isError && entries.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No audit log entries found.</p>
            {hasFilters && (
              <p className="text-xs mt-1">Try clearing the filters.</p>
            )}
          </div>
        )}

        {!isLoading && !isError && entries.map((entry) => (
          <AuditEntryRow key={entry.id} entry={entry} />
        ))}
      </div>

      {!isLoading && !isError && (entries.length > 0 || offset > 0) && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Showing {offset + 1}–{offset + entries.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={!hasMore}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
